import { Box, ListItem, PopperProps, Radio } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import * as React from "react";
import { Dispatch, SetStateAction, useMemo } from "react";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import { DGT_Pagination } from "@/utils/utils";
import { AsyncSelectOption } from "./AsyncSelect";
import ErrorHelper from "./ErrorWrapper";
import Flex, { FlexProps } from "./Flex";
import { inputLabel } from "./MaskInput";
import { useFieldLock } from "./ReadOnlyFields";
import { CHART_DIMENSIONS } from "./app/provider/patient/chart";
import { PRIMITIVE } from "./ds";

interface AsyncSelectScrollProps<T extends AsyncSelectOption> {
  label?: string;
  /** Current page of options; the popup accumulates pages across scrolls. */
  options?: T[];
  loading?: boolean;
  value?: T | null;
  onChange: (newValue: T | null) => void;
  formatKeys?: { val: string; label: string };
  onSearch?: (v: string) => void;
  flexProps?: FlexProps;
  required?: boolean;
  error?: string | boolean;
  handleBlur?: any;
  placeholder?: string;
  name?: string;
  enableFilterOption?: boolean;
  optionsWithCheckbox?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  renderCustomLabel?: (opt: AsyncSelectOption) => React.ReactNode;
  renderSelectedValue?: (opt: AsyncSelectOption) => React.ReactNode;
  disableSortOptions?: boolean;
  forcePopupIcon?: boolean;
  CustomPopperComponent?: React.ComponentType<PopperProps>;
  isMatchButtonHeight?: boolean;
  disablePortal?: boolean;
  pagination: DGT_Pagination;
  setPagination: Dispatch<SetStateAction<DGT_Pagination>>;
  totalElements?: number;
}

/**
 * AsyncSelectScroll — {@link AsyncSelect} with infinite-scroll options.
 *
 * The caller owns `pagination`/`setPagination` and feeds each page's `options`
 * plus `totalElements`; the listbox accumulates pages and requests the next one
 * when scrolled near its bottom, stopping (no spinner, no fetch) once every
 * record is loaded.
 *
 * @author [Rajkumar Rathod]
 */
function AsyncSelectScroll<T extends AsyncSelectOption>({
  label,
  options = [],
  loading = false,
  value = null,
  onChange,
  formatKeys = { val: "value", label: "label" },
  flexProps,
  onSearch,
  required,
  error,
  placeholder = "",
  handleBlur,
  name,
  enableFilterOption,
  disabled,
  readOnly,
  optionsWithCheckbox = false,
  renderCustomLabel,
  renderSelectedValue,
  disableSortOptions,
  forcePopupIcon = true,
  CustomPopperComponent,
  isMatchButtonHeight = false,
  disablePortal = false,
  pagination,
  setPagination,
  totalElements,
}: Readonly<AsyncSelectScrollProps<T>>) {
  const [open, setOpen] = React.useState(false);
  const lock = useFieldLock(disabled || readOnly);
  const idKey = formatKeys.val ?? "value";

  const { items, onScroll } = useInfiniteScroll<T>({
    pagination,
    setPagination,
    content: options,
    totalElements,
    isFetching: loading,
    dedupeKey: idKey,
  });

  const handleInputChange = (_: any, newInputValue: string, reason: string) => {
    if (reason === "input") onSearch?.(newInputValue);
  };

  const CustomHandleBlur = (e: React.FocusEvent<HTMLDivElement, Element>) => {
    onSearch?.("");
    handleBlur?.(e);
  };

  const listOptions = useMemo(() => {
    const base = disableSortOptions
      ? items
      : [...items].sort((a, b) =>
          a?.[formatKeys.label]?.localeCompare(b?.[formatKeys.label])
        );
    return base.filter((a) => value?.[idKey] !== a?.[idKey]);
  }, [items, disableSortOptions, formatKeys.label, idKey, value]);

  return (
    <form autoComplete="off">
      <Flex
        {...flexProps}
        flexDirection={flexProps?.flexDirection ?? "column"}
        sx={{ height: `${CHART_DIMENSIONS.controlHeight} !important` }}
      >
        <ErrorHelper error={error}>
          <Autocomplete
            disablePortal={disablePortal}
            sx={
              isMatchButtonHeight
                ? {
                    "& .MuiOutlinedInput-root": {
                      height: CHART_DIMENSIONS.controlHeight,
                      minHeight: CHART_DIMENSIONS.controlHeight,
                      padding: "0 8px !important",
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiOutlinedInput-input": {
                      height: "100% !important",
                      padding: "0 !important",
                      display: "flex",
                      alignItems: "center",
                      boxSizing: "border-box",
                    },
                    "& .MuiInputBase-root": {
                      height: CHART_DIMENSIONS.controlHeight,
                    },
                    "& .MuiFormControl-root": {
                      height: CHART_DIMENSIONS.controlHeight,
                    },
                  }
                : undefined
            }
            autoHighlight={false}
            autoSelect={false}
            forcePopupIcon={lock.readOnly ? false : forcePopupIcon}
            disabled={lock.disabled}
            readOnly={lock.readOnly}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            disableClearable={false}
            fullWidth
            onBlur={CustomHandleBlur}
            PopperComponent={CustomPopperComponent}
            value={value}
            slotProps={{ popper: { sx: { zIndex: 99999 } } }}
            ListboxProps={{
              onScroll: (e: React.UIEvent<HTMLElement>) =>
                onScroll(e.currentTarget),
            }}
            onInputChange={handleInputChange}
            onChange={(_, val) => {
              onSearch?.("");
              return onChange(val);
            }}
            renderOption={(props, option) => (
              <ListItem
                {...props}
                key={option.value}
                sx={{ fontSize: `${PRIMITIVE.typography.fontSize.body.b5}px` }}
              >
                {optionsWithCheckbox && (
                  <Radio
                    checked={value?.[idKey] === option?.[idKey]}
                    size="small"
                    sx={{ p: 0, mr: 1 }}
                  />
                )}
                {renderCustomLabel
                  ? renderCustomLabel(option)
                  : option[formatKeys.label]}
              </ListItem>
            )}
            filterOptions={enableFilterOption ? (opt) => opt : undefined}
            isOptionEqualToValue={(option, v) => option?.[idKey] === v?.[idKey]}
            getOptionLabel={(option) => option?.[formatKeys.label]}
            options={listOptions}
            loading={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={inputLabel(label, required)}
                sx={{ fontSize: `${PRIMITIVE.typography.fontSize.body.b5}px` }}
                InputLabelProps={{ shrink: true }}
                name={name}
                error={!!error}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.preventDefault();
                }}
                onBlur={handleBlur}
                placeholder={placeholder}
                InputProps={{
                  ...params.InputProps,
                  startAdornment:
                    renderSelectedValue && value ? (
                      <Box
                        sx={{
                          fontSize: `${PRIMITIVE.typography.fontSize.body.b5}px`,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mr: 1,
                        }}
                      >
                        {renderSelectedValue(value)}
                      </Box>
                    ) : undefined,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </ErrorHelper>
      </Flex>
    </form>
  );
}

export default AsyncSelectScroll;
